import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { ActionsToggle } from './Buttons';

export interface ActionItem {
    label: string;
    onClick: () => void;
    className?: string; 
    itemClassName?: string;
    disabled?: boolean;
}

interface ActionListProps {
    onDelete?: () => void;
    onEdit?: () => void;
    editLink?: string;
    customActions?: ActionItem[];
    disabled?: boolean;
}

const ActionList: React.FC<ActionListProps> = ({ onDelete, onEdit, editLink, customActions, disabled = false }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMenuPositioned, setIsMenuPositioned] = useState(false);
    const [menuPosition, setMenuPosition] = useState<React.CSSProperties>({});
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const portalContainerRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    function updateMenuPosition(): boolean {
        if (!buttonRef.current || !menuRef.current) {
            return false;
        }

        const viewportPadding = 8;
        const triggerRect = buttonRef.current.getBoundingClientRect();
        const menuHeight = menuRef.current.offsetHeight;
        const menuWidth = menuRef.current.offsetWidth;

        if (menuHeight <= 0 || menuWidth <= 0) {
            return false;
        }

        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const showAbove = spaceBelow < menuHeight + viewportPadding;

        let top = showAbove ? triggerRect.top - menuHeight : triggerRect.bottom;
        top = Math.max(viewportPadding, Math.min(top, window.innerHeight - menuHeight - viewportPadding));

        const desiredLeft = triggerRect.left;
        const minLeft = viewportPadding;
        const maxLeft = window.innerWidth - menuWidth - viewportPadding;
        const left = Math.max(minLeft, Math.min(desiredLeft, Math.max(minLeft, maxLeft)));

        setMenuPosition({ position: 'fixed', top: `${top}px`, left: `${left}px` });
        return true;
    }

    function applyMenuPosition() {
        const positioned = updateMenuPosition();
        if (positioned) {
            setIsMenuPositioned(true);
        }
    }

    function handleCustomActionClick(event: React.MouseEvent<HTMLButtonElement>) {
        setIsDropdownOpen(false);
        const actionLabel = event.currentTarget.dataset.actionLabel;
        if (!actionLabel || !customActions) {
            return;
        }
        const action = customActions.find(item => item.label === actionLabel);
        action?.onClick();
    }

    function handleEditClick() {
        setIsDropdownOpen(false);
        onEdit?.();
    }

    // Handle delete action
    function handleDelete(e: React.MouseEvent) {
        e.preventDefault();
        setIsDropdownOpen(false);
        onDelete?.();
    }

    const renderCustomActions = (): React.ReactNode => {
        if (!customActions) {
            return null;
        }

        return customActions.map(action => {
            const itemClassName = action.itemClassName ?? '';
            const actionClassName = action.className ?? '';

            return (
                <li key={action.label} className={`actions__item ${itemClassName}`}>
                    <button
                        role="menuitem"
                        type="button"
                        data-action-label={action.label}
                        onClick={handleCustomActionClick}
                        className={`actions__link actions__link--button ${actionClassName}`}
                        disabled={action.disabled}
                    >
                        <span className="actions__label">{action.label}</span>
                    </button>
                </li>
            );
        });
    };

    const renderEditAction = (): React.ReactNode => {
        if (!onEdit && !editLink) {
            return null;
        }

        if (onEdit) {
            return (
                <li className="actions__item actions__item--edit">
                    <button role="menuitem" type="button" onClick={handleEditClick} className="actions__link actions__link--button actions__link--edit" title="Edit">
                        <span className="actions__label">Edit</span>
                    </button>
                </li>
            );
        }

        const linkTo = editLink ?? '';
        return (
            <li className="actions__item actions__item--edit">
                <Link role="menuitem" to={linkTo} className="actions__link" data-placement="top" data-toggle="tooltip" title="Edit">
                    <span className="actions__label">Edit</span>
                </Link>
            </li>
        );
    };

    const renderDefaultActions = (): React.ReactNode => {
        let deleteAction: React.ReactNode = null;
        if (onDelete) {
            deleteAction = (
                <li className="actions__item actions__item--delete">
                    <button
                        role="menuitem"
                        onClick={handleDelete}
                        className="actions__link actions__link--button actions__link--delete table-actions__delete"
                        data-placement="top"
                        data-toggle="tooltip"
                        title="Delete"
                        type="button"
                    >
                        <span className="actions__label">Delete</span>
                    </button>
                </li>
            );
        }

        return (
            <>
                {/* Default actions: Edit and Delete */}
                {renderEditAction()}
                {deleteAction}
            </>
        );
    };

    let actionsContent: React.ReactNode;
    if (customActions) {
        actionsContent = renderCustomActions();
    } else {
        actionsContent = renderDefaultActions();
    }

    // Close the dropdown if clicked outside of both the in-row container and the portal
    function handleClickOutside(event: MouseEvent) {
        const target = event.target as Node;
        const clickedOutsideRow = dropdownRef.current && !dropdownRef.current.contains(target);
        const clickedOutsidePortal = portalContainerRef.current && !portalContainerRef.current.contains(target);

        // If portal exists, consider it when deciding whether the click was outside
        if (portalContainerRef.current) {
            if (clickedOutsideRow && clickedOutsidePortal) {
                setIsDropdownOpen(false);
            }
        } else if (clickedOutsideRow) {
            setIsDropdownOpen(false);
        }
        else {
            // If portal doesn't exist yet, just check if click was outside the row
            setIsDropdownOpen(false);
        }
    }

    // Add event listener for detecting clicks outside the dropdown
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const buttonElement = buttonRef.current;
        if (!buttonElement) {
            return undefined;
        }

        function handleToggleClick() {
            setIsDropdownOpen(previousState => !previousState);
        }

        buttonElement.addEventListener('click', handleToggleClick);
        return () => {
            buttonElement.removeEventListener('click', handleToggleClick);
        };
    }, []);

    // Create a portal container when component mounts
    useEffect(() => {
        const el = document.createElement('div');
        el.className = 'actions__portal';
        portalContainerRef.current = el;
        document.body.appendChild(el);
        return () => {
            // Prefer optional chaining and the DOM `remove()` method for clarity
            portalContainerRef.current?.remove();
            portalContainerRef.current = null;
        };
    }, []);

    // After the menu renders, compute a viewport-clamped fixed position.
    useLayoutEffect(() => {
        if (!isDropdownOpen || !buttonRef.current || !menuRef.current) {
            return;
        }

        let raf1 = 0;
        let raf2 = 0;

        const positionMenu = () => applyMenuPosition();

        positionMenu();
        raf1 = window.requestAnimationFrame(() => {
            positionMenu();
            raf2 = window.requestAnimationFrame(positionMenu);
        });

        return () => {
            if (raf1) {
                window.cancelAnimationFrame(raf1);
            }
            if (raf2) {
                window.cancelAnimationFrame(raf2);
            }
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        if (!isDropdownOpen) {
            setIsMenuPositioned(false);
            setMenuPosition({});
            return undefined;
        }

        setIsMenuPositioned(false);

        const handleReposition = () => {
            window.requestAnimationFrame(() => {
                applyMenuPosition();
            });
        };

        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [isDropdownOpen]);

    return (
        <div className="actions" ref={dropdownRef}>
            <div className="actions__list">
                <ActionsToggle
                    ref={buttonRef}
                    isExpanded={isDropdownOpen}
                    ariaControls="actions-menu"
                    disabled={disabled}
                />
            </div>

            {isDropdownOpen && portalContainerRef.current && ReactDOM.createPortal(

                <div style={{ ...menuPosition, visibility: isMenuPositioned ? 'visible' : 'hidden' }} className="actions__menu" ref={menuRef}>
                    
                    <h4 className="actions__title">Actions</h4>
                    <hr />
                    <ul className="actions__list--menu">
                        {/* Custom actions (if provided, render only these) */}
                        {actionsContent}
                    </ul>
                </div>,
                portalContainerRef.current
            )}
        </div>
    );
};

export default ActionList;
